// R-131-FIN-DECISION-A — technology preset registry.

export { presetFor as gasCombinedCyclePresetFor, GAS_CC_BENCHMARKS } from './gasCombinedCycle';
export { presetFor as gasSimpleCyclePresetFor, GAS_SC_BENCHMARKS } from './gasSimpleCycle';
export { presetFor as nuclearSmrPresetFor, NUCLEAR_SMR_BENCHMARKS } from './nuclearSmr';
export { presetFor as nuclearLargePresetFor, NUCLEAR_LARGE_BENCHMARKS } from './nuclearLarge';
export { presetFor as solarBessPresetFor, SOLAR_BESS_BENCHMARKS } from './solarBess';
export { presetFor as windBessPresetFor, WIND_BESS_BENCHMARKS } from './windBess';
export { presetFor as gridTariffPresetFor, GRID_TARIFF_BENCHMARKS } from './gridTariff';
export {
  fuelCellSofcPresetFor,
  fuelCellPafcPresetFor,
  fuelCellPemPresetFor,
  fuelCellMcfcPresetFor,
  CURATED_FUEL_CELL_PACKAGES,
  DEFAULT_PACKAGE_BY_TECHNOLOGY_ID,
} from './stationaryFuelCell';

export type { PathwayPresetOptions, PresetForFn } from './shared';
export {
  validatePathwayDefaults,
  pathwayJsonRoundTrip,
  resolveCapacityMw,
} from './shared';

import type { TechnologyId } from '../types';
import type { PresetForFn } from './shared';
import { presetFor as gasCombinedCyclePresetFor } from './gasCombinedCycle';
import { presetFor as gasSimpleCyclePresetFor } from './gasSimpleCycle';
import { presetFor as nuclearSmrPresetFor } from './nuclearSmr';
import { presetFor as nuclearLargePresetFor } from './nuclearLarge';
import { presetFor as solarBessPresetFor } from './solarBess';
import { presetFor as windBessPresetFor } from './windBess';
import { presetFor as gridTariffPresetFor } from './gridTariff';
import {
  fuelCellSofcPresetFor,
  fuelCellPafcPresetFor,
  fuelCellPemPresetFor,
  fuelCellMcfcPresetFor,
} from './stationaryFuelCell';

/**
 * Lookup preset factory by technology id.
 *
 * Q7 — `mixed` HAS NO FACTORY. It used to map to the hybrid-bridge preset, which
 * the cashflow engine cannot represent: the engine sums every construction
 * phase from the first start (COD 2034 for a bridge that powers up in 2031),
 * reads energy from only the FIRST operation phase, and charges gas O&M, fuel,
 * transmission tariff and demand charge simultaneously in every year. The
 * result — LCOE $148/MWh, NPV −$416M, worse than BOTH of its own legs — was
 * arithmetic, not a finding about hybrids, and it was the DEFAULT selection for
 * the hybrid, undecided and grid-only postures.
 *
 * `mixed` survives as a TechnologyId because it is still a meaningful label for
 * a phase that carries a `sourceMix` — a CONCURRENT blend, which the engine
 * does represent (`blendPhaseEconomics`). What it no longer names is a
 * SEQUENTIAL bridge. Restoring one needs per-operation-phase COD, energy and
 * opex in the engine; until then there is no honest factory to point at, and
 * `Partial` makes that absence something callers must handle rather than a
 * runtime crash.
 */
export const PRESET_BY_TECHNOLOGY: Partial<Record<TechnologyId, PresetForFn>> = {
  'gas-cc': gasCombinedCyclePresetFor,
  'gas-sc': gasSimpleCyclePresetFor,
  'nuclear-smr': nuclearSmrPresetFor,
  'nuclear-large': nuclearLargePresetFor,
  'solar-bess': solarBessPresetFor,
  'wind-bess': windBessPresetFor,
  'grid-tariff': gridTariffPresetFor,
  'fuel-cell-sofc': fuelCellSofcPresetFor,
  'fuel-cell-pafc': fuelCellPafcPresetFor,
  'fuel-cell-pem': fuelCellPemPresetFor,
  'fuel-cell-mcfc': fuelCellMcfcPresetFor,
};

export const ALL_PRESET_TECHNOLOGIES = Object.keys(PRESET_BY_TECHNOLOGY) as TechnologyId[];
